import React, { useCallback, useState, useEffect } from 'react';
import { Alert, Box, Checkbox, FormControlLabel, Paper, Tooltip, Typography } from '@mui/material';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import CustomButton from '../buttons/CustomButton';
import styles from './CompanyDataExport.module.css';
import { DataProviderInstance } from '../../api/DataProvider';
import ExportHistory from './ExportHistory';
import { attachmentsEstimateLabel } from './exportJob';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import usePlanLock from '../../hooks/usePlanLock';
import { SUBSCRIPTION_PERMISSIONS } from '../../consts/SubscriptionFeatures';

/**
 * Bloco "Exportar meus dados" + histórico. Reúne a opção de incluir anexos,
 * o botão de exportação (com a estimativa de tamanho) e a lista de exportações.
 * A página (Export.js) coordena o histórico: `onExportCreated(exportId)` avisa
 * que uma exportação foi criada aqui (ela recarrega a lista via `refreshKey`
 * e baixa o arquivo sozinha ao terminar, pelo `onJobFinished` do histórico);
 * `onHistoryUpdate` repassa a lista carregada pelo histórico.
 */
const CompanyDataExport = ({ refreshKey = 0, onHistoryUpdate, onExportCreated, onJobFinished }) => {
    const { CreateExportJob, GetExportEstimate } = DataProviderInstance();
    const { canAccess } = useRoleAccess();
    const [error, setError] = useState(null);
    const [history, setHistory] = useState([]);
    const [includeAttachments, setIncludeAttachments] = useState(false);
    const [estimate, setEstimate] = useState(null);

    // Só a pessoa responsável pela conta exporta (ACCESS_AREAS.dataExport, igual ao backend)
    const canExportData = canAccess('dataExport');
    // Cadeado por plano (data_export, só frontend): o bloco continua visível e o
    // botão de gerar abre o modal de upgrade. O histórico e o encerramento de
    // conta (CompanyClosureSection) não passam por aqui.
    const lock = usePlanLock(SUBSCRIPTION_PERMISSIONS.DATA_EXPORT, 'A exportação completa dos dados');

    // Load attachments estimate
    useEffect(() => {
        GetExportEstimate((data) => {
            if (!data.error) {
                setEstimate(data);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // O histórico carrega a lista (ao montar e a cada recarga) e a repassa.
    const handleHistoryUpdate = useCallback((items) => {
        setHistory(items);
        if (onHistoryUpdate) onHistoryUpdate(items);
    }, [onHistoryUpdate]);

    const attachmentsCount = estimate?.attachments?.count || 0;
    const hasAttachments = attachmentsCount > 0;

    const startExport = async () => {
        try {
            setError(null);
            // Generate temporary ID
            const tempId = 'temp-' + new Date().getTime();

            // Add pending export to history immediately
            setHistory(prev => [{
                id: tempId,
                status: 'pending',
                progress: 0,
                created_at: new Date().toISOString(),
                include_attachments: includeAttachments ? 1 : 0
            }, ...prev]);

            // Make API call
            CreateExportJob(includeAttachments, (data) => {
                if (data.error) {
                    // Remove temporary entry on error
                    // (dentro do callback, um throw não chegaria ao catch abaixo)
                    setHistory(prev => prev.filter(item => item.id != tempId));
                    setError(data.error);
                    return;
                }
                // Update history with real exportId
                setHistory(prev => prev.map(item =>
                    item.id == tempId ? {
                        ...item,
                        id: data.exportId
                    } : item
                ));
                // A página recarrega o histórico (a linha real entra na hora) e
                // marca o job para baixar sozinho quando terminar.
                if (onExportCreated) onExportCreated(data.exportId);
            });
        } catch (err) {
            setError(err.message);
        }
    };
    const onExportClick = lock.guard(startExport);

    const hasActiveExport = history.some(item =>
        ['pending', 'processing'].includes(item.status)
    );

    const roleTooltip = !canExportData ? "Só a pessoa responsável pela conta pode exportar os dados" : "";

    const exportButton = error ? (
        <Alert severity="error" className={styles.alert}>
            {error}
            <Box sx={{ mt: 1 }}>
                <Tooltip title={roleTooltip} arrow>
                    <span>
                        <CustomButton
                            style="secondary"
                            variant="contained"
                            label="Tentar Novamente"
                            icon={<CloudDownloadOutlinedIcon fontSize="small" />}
                            onClick={onExportClick}
                            enabled={(lock.locked || !hasActiveExport) && canExportData}
                            disabled={!canExportData}
                        />
                    </span>
                </Tooltip>
            </Box>
        </Alert>
    ) : (
        <Tooltip title={roleTooltip} arrow>
            <span>
                <CustomButton
                    style="secondary"
                    variant="contained"
                    label="Exportar dados"
                    icon={<CloudDownloadOutlinedIcon />}
                    onClick={onExportClick}
                    enabled={(lock.locked || !hasActiveExport) && canExportData}
                    disabled={!canExportData}
                />
            </span>
        </Tooltip>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
                {lock.banner && <Box sx={{ mb: 1.5 }}>{lock.banner}</Box>}
                {lock.modal}
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Exportar meus dados
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    Baixe todos os dados da clínica: pacientes, agenda, financeiro e o que mais for seu.
                </Typography>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={includeAttachments}
                            onChange={(e) => setIncludeAttachments(e.target.checked)}
                            disabled={!hasAttachments}
                        />
                    }
                    label={`Incluir anexos e fotos${attachmentsEstimateLabel(estimate)}`}
                />

                <Box sx={{ mt: 1 }}>
                    {exportButton}
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    Se você continuar nesta tela, o download começa sozinho quando a exportação terminar.
                    O arquivo fica disponível por 24 horas no histórico abaixo.
                </Typography>
            </Paper>

            <ExportHistory refreshKey={refreshKey} onHistoryUpdate={handleHistoryUpdate} onJobFinished={onJobFinished} />
        </Box>
    );
};

export default CompanyDataExport;
